import { useState, useEffect, useCallback } from 'react'
import { motion } from 'framer-motion'
import { Filter, TrendingDown, Loader2 } from 'lucide-react'
import { Button } from '../components/ui/Button'
import ProjectCard from '../components/molecules/ProjectCard'
import SearchBar from '../components/molecules/SearchBar'
import CategoryTabs from '../components/molecules/CategoryTabs'
import ViewToggle from '../components/molecules/ViewToggle'
import ViewModeToggle from '../components/molecules/ViewModeToggle'
import { Skeleton } from '../components/ui/Skeleton'
import useInfiniteProjects from '../hooks/useInfiniteProjects'
import useUserProjects from '../hooks/useUserProjects'
import { useAuth } from '../hooks/useAuth'
import { PROJECT_STATUS } from '../constants/index.js'
import SignInModal from '../components/auth/SignInModal'

const ProjectsPage = () => {
  const { user, hasCapability } = useAuth()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [viewMode, setViewMode] = useState('grid')
  const [projectViewMode, setProjectViewMode] = useState('all') // 'all' or 'my'
  const [statusFilter, setStatusFilter] = useState(null) // For my projects mode - null means active & completed
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false)

  // Project categories for "All Projects" mode
  const projectCategories = [
    { id: 'all', label: 'All Projects' },
    { id: 'medical', label: 'Medical Relief' },
    { id: 'education', label: 'Education' },
    { id: 'technology', label: 'Technology' },
    { id: 'environment', label: 'Environment' },
    { id: 'community', label: 'Community' }
  ]

  // Status categories for "My Projects" mode
  const statusCategories = [
    { id: null, label: 'Active & Completed' }, // Default - shows active and completed
    { id: 'active', label: 'Active Only' },
    { id: 'completed', label: 'Completed Only' }
  ]

  // Get appropriate categories based on view mode
  const categories = projectViewMode === 'my' ? statusCategories : projectCategories

  // Public projects hook (for "All Projects" mode)
  const {
    projects: publicProjects,
    totalCount: publicTotalCount,
    error: publicError,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading: publicLoading,
    isError: publicIsError
  } = useInfiniteProjects({
    category: selectedCategory,
    search: searchTerm,
    limit: 20
  })

  // User projects hook (for "My Projects" mode)
  const {
    projects: userProjects,
    totalCount: userTotalCount,
    error: userError,
    isLoading: userLoading,
    isError: userIsError,
    isAuthenticated
  } = useUserProjects(statusFilter === 'all' ? null : statusFilter)

  // Determine which data to use based on view mode
  const projects = projectViewMode === 'my' ? userProjects : publicProjects
  const totalCount = projectViewMode === 'my' ? userTotalCount : publicTotalCount
  const error = projectViewMode === 'my' ? userError : publicError
  const isLoading = projectViewMode === 'my' ? userLoading : publicLoading
  const isError = projectViewMode === 'my' ? userIsError : publicIsError

  // Intersection Observer for infinite scroll (only for public projects)
  const [loadMoreRef, setLoadMoreRef] = useState(null)

  const loadMoreCallback = useCallback((entries) => {
    const [entry] = entries
    if (entry.isIntersecting && hasNextPage && !isFetchingNextPage && projectViewMode === 'all') {
      fetchNextPage()
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, projectViewMode])

  useEffect(() => {
    if (!loadMoreRef || projectViewMode === 'my') return

    const observer = new IntersectionObserver(loadMoreCallback, {
      threshold: 0.1,
      rootMargin: '100px'
    })

    observer.observe(loadMoreRef)

    return () => {
      if (loadMoreRef) {
        observer.unobserve(loadMoreRef)
      }
    }
  }, [loadMoreRef, loadMoreCallback, projectViewMode])

  // Handler functions
  const handleViewModeChange = (mode) => {
    if (mode === 'my' && !isAuthenticated) {
      setIsSignInModalOpen(true)
      return
    }
    setProjectViewMode(mode)
    // Reset filters when switching modes
    if (mode === 'my') {
      setStatusFilter(null) // Default to Active & Completed
      setSearchTerm('') // Clear search for my projects
    } else {
      setSelectedCategory('all')
    }
  }

  const handleCategoryChange = (categoryId) => {
    if (projectViewMode === 'my') {
      setStatusFilter(categoryId)
    } else {
      setSelectedCategory(categoryId)
    }
  }

  const handleFavorite = (projectId) => {
    console.log('Favoriting project:', projectId)
    // TODO: Implement favorite functionality
  }

  // Check if user can create projects (for showing My Projects option)
  const canCreateProjects = hasCapability('create_project')

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="space-y-8"
    >
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-h2 font-heading text-neutral-900 gradient-text">
            {projectViewMode === 'my' ? 'My Projects' : 'Projects'}
          </h1>
          <p className="text-p1-r text-neutral-600">
            {projectViewMode === 'my'
              ? 'Manage and track your active and completed projects'
              : 'Discover and support amazing projects making a difference'
            }
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canCreateProjects && (
            <ViewModeToggle
              viewMode={projectViewMode}
              onViewModeChange={handleViewModeChange}
            />
          )}
          <Button variant="outline" className="flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filter
          </Button>
        </div>
      </motion.div>

      {/* Search and Categories */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="space-y-6"
      >
        {/* Search Bar - only show for "All Projects" mode */}
        {projectViewMode === 'all' && (
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search projects..."
            className="max-w-md mx-auto"
          />
        )}

        {/* View Toggle */}
        <div className="flex justify-center">
          <ViewToggle
            view={viewMode}
            onViewChange={setViewMode}
          />
        </div>

        {/* Category/Status Tabs */}
        <CategoryTabs
          categories={categories}
          selectedCategory={projectViewMode === 'my' ? statusFilter : selectedCategory}
          onCategoryChange={handleCategoryChange}
        />
      </motion.div>

      {/* Loading State */}
      {isLoading && (
        <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4' : 'space-y-4'}>
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="space-y-3">
              <Skeleton className={viewMode === 'grid' ? 'h-36 w-full rounded-lg' : 'h-24 w-full rounded-lg'} />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-2/3" />
            </div>
          ))}
        </div>
      )}

      {/* Error State */}
      {isError && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center py-16"
        >
          <div className="w-20 h-20 bg-gradient-to-br from-secondary-red/20 to-secondary-red/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <TrendingDown className="w-10 h-10 text-secondary-red" />
          </div>
          <h3 className="text-h3 font-heading text-neutral-900 mb-3">
            Failed to load projects
          </h3>
          <p className="text-p1-r text-neutral-600 max-w-md mx-auto mb-6">
            {error?.message || 'Something went wrong while loading projects. Please try again.'}
          </p>
          <Button onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </motion.div>
      )}

      {/* Projects Display */}
      {!isLoading && !isError && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className={
            viewMode === 'grid'
              ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4'
              : 'space-y-4'
          }
        >
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: Math.min(index * 0.05, 0.5) }}
            >
              <ProjectCard
                project={project}
                variant={viewMode}
                onFavorite={handleFavorite}
                onSupport={handleFavorite} // TODO: Implement support functionality
                isFavorited={false} // TODO: Implement favorites state
                isOwner={projectViewMode === 'my'}
              />
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Infinite Scroll Trigger - only for "All Projects" mode */}
      {projectViewMode === 'all' && hasNextPage && (
        <div
          ref={setLoadMoreRef}
          className="flex justify-center py-8"
        >
          {isFetchingNextPage && (
            <div className="flex items-center gap-2 text-neutral-600">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Loading more projects...</span>
            </div>
          )}
        </div>
      )}

      {/* Authentication Required Message for My Projects */}
      {projectViewMode === 'my' && !isAuthenticated && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center py-16"
        >
          <div className="w-20 h-20 bg-gradient-to-br from-primary-teal/20 to-primary-teal/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <TrendingDown className="w-10 h-10 text-primary-teal" />
          </div>
          <h3 className="text-h3 font-heading text-neutral-900 mb-3">
            Authentication Required
          </h3>
          <p className="text-p1-r text-neutral-600 max-w-md mx-auto">
            Please log in to view and manage your projects.
          </p>
        </motion.div>
      )}

      {/* Empty State */}
      {!isLoading && !isError && projects.length === 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center py-16"
        >
          <div className="w-20 h-20 bg-gradient-to-br from-neutral-200 to-neutral-300 rounded-full flex items-center justify-center mx-auto mb-6">
            <TrendingDown className="w-10 h-10 text-neutral-400" />
          </div>
          <h3 className="text-h3 font-heading text-neutral-900 mb-3">
            {projectViewMode === 'my' ? 'No projects found' : 'No projects found'}
          </h3>
          <p className="text-p1-r text-neutral-600 max-w-md mx-auto">
            {projectViewMode === 'my'
              ? statusFilter === 'active'
                ? 'You don\'t have any active projects yet. Create your first project to get started!'
                : statusFilter === 'completed'
                  ? 'You don\'t have any completed projects yet.'
                  : 'You haven\'t created any projects yet. Start your first project to begin your journey!'
              : 'Try adjusting your search terms or explore different categories to discover amazing projects'
            }
          </p>
        </motion.div>
      )}

      {/* Results Summary */}
      {!isLoading && !isError && projects.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="text-center pt-8 text-p2-r text-neutral-600"
        >
          {projectViewMode === 'my'
            ? `Showing ${projects.length} of your projects`
            : `Showing ${projects.length} projects${totalCount > projects.length ? ` of ${totalCount} total` : ''}`
          }
        </motion.div>
      )}

      {/* Sign In Modal */}
      <SignInModal
        isOpen={isSignInModalOpen}
        onClose={() => setIsSignInModalOpen(false)}
      />
    </motion.div>
  )
}

export default ProjectsPage
